import React, { useState, useEffect, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import API_BASE_URL from "./config";
import "./EventAnalytics.css";

const formatDay = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

function StatTile({ label, value }) {
  return (
    <div className="ea-tile">
      <div className="ea-tile-value">{value}</div>
      <div className="ea-tile-label">{label}</div>
    </div>
  );
}

function TrendChart({ title, data }) {
  const chartData = data.map((d) => ({ ...d, label: formatDay(d.date) }));
  return (
    <div className="ea-chart-card">
      <h4>{title}</h4>
      {chartData.length === 0 ? (
        <p className="ea-empty">No activity yet in this window.</p>
      ) : (
        <ResponsiveContainer width="100%" height={160}>
          <LineChart data={chartData} margin={{ top: 6, right: 12, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6b7280" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#6b7280" }} />
            <Tooltip />
            <Line type="monotone" dataKey="count" stroke="#7c3aed" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function TopImagesRow({ title, images, emptyText }) {
  return (
    <div className="ea-top-row">
      <h4>{title}</h4>
      {images.length === 0 ? (
        <p className="ea-empty">{emptyText}</p>
      ) : (
        <div className="ea-thumb-strip">
          {images.map((img) => (
            <div key={img.id} className="ea-thumb" title={img.original_filename}>
              <img src={img.image_url} alt={img.original_filename || "photo"} />
              <span className="ea-thumb-count">{img.count}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EventAnalytics({ eventId }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/analytics`);
      if (res.ok) setData(await res.json());
    } catch {
      // Leave data null — the section just shows nothing rather than an error banner.
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  if (loading) return <div className="ea-loading">Loading analytics...</div>;
  if (!data) return null;

  // An error body, an older event, or a partial response must not take the page
  // down — every section is optional and renders as zero when absent.
  const views = data.views || {};
  const downloads = data.downloads || {};
  const faceSearch = data.faceSearch || {};
  const votes = data.votes || {};

  return (
    <div className="ea-wrap">
      <div className="ea-tiles">
        <StatTile label="Gallery Views" value={views.total ?? 0} />
        <StatTile label="Unique Viewers" value={views.unique ?? 0} />
        <StatTile label="Downloads" value={downloads.total ?? 0} />
        <StatTile label="Face Searches" value={faceSearch.total ?? 0} />
      </div>

      <div className="ea-charts">
        <TrendChart title="Views over time" data={views.byDay || []} />
        <TrendChart title="Face searches over time" data={faceSearch.byDay || []} />
      </div>

      <TopImagesRow
        title="📥 Most Downloaded"
        images={downloads.topImages || []}
        emptyText="No downloads yet."
      />
      <TopImagesRow
        title="❤️ Most Selected"
        images={votes.topSelected || []}
        emptyText="No votes yet."
      />
    </div>
  );
}

export default EventAnalytics;
